// ChartCard — wrapper for chart sections with title, subtitle, and content area

import type { ReactNode } from "react";

interface ChartCardProps {
  title: ReactNode;
  subtitle?: string;
  /** Optional right-side header content (e.g. badges, buttons) */
  headerRight?: ReactNode;
  children: ReactNode;
  /** Minimum height for the chart area when data is present */
  minHeight?: number;
  /**
   * When true, the card collapses to a compact height suited to an empty-state
   * placeholder instead of reserving `minHeight` pixels of whitespace.
   */
  empty?: boolean;
  /** Minimum height used when `empty` is true (default: 96). */
  emptyMinHeight?: number;
}

export default function ChartCard({
  title,
  subtitle,
  headerRight,
  children,
  minHeight = 240,
  empty = false,
  emptyMinHeight = 96,
}: ChartCardProps) {
  const contentMinHeight = empty ? emptyMinHeight : minHeight;
  return (
    <div className="glass rounded-2xl overflow-hidden">
      {/* Card header */}
      <div className="px-4 md:px-5 pt-4 md:pt-5 pb-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 border-b border-theme-subtle">
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-theme-primary leading-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs text-theme-muted mt-0.5 leading-snug">
              {subtitle}
            </p>
          )}
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </div>

      {/* Chart content */}
      <div className="p-4 md:p-5" style={{ minHeight: contentMinHeight }}>
        {children}
      </div>
    </div>
  );
}
